import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingScreen from '../components/LoadingScreen';
import Toast, { ToastType } from '../components/Toast';
import { AvatarGeneration, FilterState } from '../types/api';
import { localStorageManager, getLikedImages, toggleLikedImage } from '../utils/localStorage';
import { RateLimitStatus as RateLimitStatusType } from '../types/api';
import { createErrorInfo, ErrorInfo } from '../components/ErrorState';
import { apiService, ApiServiceError } from '../services/apiService';
import OutOfCreditsModal from '../components/OutOfCreditsModal';
import { useOutOfCreditsModal } from '../hooks/useOutOfCreditsModal';
import EnhancedInputAvatar from '../components/EnhancedInputAvatar';
import RateLimitStatus from '../components/RateLimitStatus';
import ImageActionButton from '../components/ImageActionButton';
import HeartIcon from '../components/HeartIcon';
import MenuIcon from '../components/MenuIcon';
import ImageMenu from '../components/ImageMenu';
// import PoppingModal from '../components/PoppingModal';
// import { usePoppingModal } from '../hooks/usePoppingModal';
import { ASSETS } from '../assets/assetMapping';
import ImageWithFallback from '../components/ImageWithFallback';
import SubmitModal from '../components/SubmitModal';
import { useSubmitModal } from '../hooks/useSubmitModal';
import { mixpanelService } from '../services/mixpanelService';



// Image assets from Figma
// Unused - kept for future use
// const imgBigvuIcon = ASSETS.BIGVU_ICON;
const imgCopyIcon = ASSETS.COPY_ICON;
const imgAvatarImage = ASSETS.AVATAR_PLACEHOLDER;

function ResultsPage() {
  const navigate = useNavigate();
  
  // Out of credits modal
  const { isOpen: isOutOfCreditsOpen, openModal: openOutOfCreditsModal, closeModal: closeOutOfCreditsModal, handleBuyCredits } = useOutOfCreditsModal();
  
  // Check localStorage IMMEDIATELY (synchronously) during render
  // This prevents showing loading spinner if we need to redirect
  const initialAppState = localStorageManager.getAppState();
  const hasInitialData = initialAppState.generationHistory.length > 0;
  
  // Get generation data from localStorage (single source of truth)
  // Initialize state with data immediately to avoid loading spinner
  const [generation, setGeneration] = useState<AvatarGeneration | null>(
    hasInitialData ? initialAppState.generationHistory[0] : null
  );
  const [rateLimitStatus, setRateLimitStatus] = useState<RateLimitStatusType | null>(
    hasInitialData ? (initialAppState.rateLimitStatus || null) : null
  );
  const [generationHistory, setGenerationHistory] = useState<AvatarGeneration[]>(
    hasInitialData ? initialAppState.generationHistory.slice(1) : []
  );
  
  // Enhanced toast state
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastType, setToastType] = useState<ToastType>('success');
  const [toastTitle, setToastTitle] = useState<string | undefined>();
  const [toastPersistent, setToastPersistent] = useState(false);
  
  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationError, setGenerationError] = useState<ErrorInfo | null>(null);

  // Current input prompt
  const [currentInputPrompt, setCurrentInputPrompt] = useState('');
  const [generatingPrompt, setGeneratingPrompt] = useState('');
  const [uploadedFileBase64, setUploadedFileBase64] = useState<string | null>(null);

  // ResultPage component state (copied from ResultPage.tsx)
  // const { isOpen: isModalOpen, openModal: openPremiumModal, closeModal: closePremiumModal } = usePoppingModal();
  // Submit modal state
  const { isOpen: isSubmitModalOpen, openModal: openSubmitModal, closeModal: closeSubmitModal } = useSubmitModal();
  // Track selected image globally across all generations
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  
  // Track liked images
  const [likedImages, setLikedImages] = useState<Set<string>>(new Set());

  // Centralized mobile detection
  const [isMobileView, setIsMobileView] = useState(false);
  
  // Track menu state for each image
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  
  // Refs for menu buttons
  const menuButtonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  // Load liked images from localStorage
  useEffect(() => {
    const liked = getLikedImages();
    setLikedImages(new Set(liked));
  }, []);

  // Centralized mobile detection (copied from ResultPage)
  useEffect(() => {
    const checkMobile = () => {
      setIsMobileView(window.innerWidth < 1024); // lg breakpoint in Tailwind
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Helper function to create unique image ID (copied from ResultPage)
  const createImageId = (generationId: string, index: number) => `${generationId}-${index}`;

  // Helper function to get selected image URL (copied from ResultPage)
  const getSelectedImageUrl = (): string | null => {
    if (!selectedImageId) return null;
    
    // Check current generation
    if (generation?.s3Urls && generation.s3Urls.length > 0) {
      for (let i = 0; i < generation.s3Urls.length; i++) {
        const imageId = createImageId('current', i);
        if (imageId === selectedImageId) {
          return generation.s3Urls[i];
        }
      }
    }
    
    // Check generation history
    for (const gen of generationHistory) {
      for (let i = 0; i < gen.s3Urls.length; i++) {
        const imageId = createImageId(gen.id, i);
        if (imageId === selectedImageId) {
          return gen.s3Urls[i];
        }
      }
    }
    
    return null;
  };

  // Helper function to get generation data for selected image
  const getSelectedImageGeneration = (): AvatarGeneration | null => {
    if (!selectedImageId) return null;
    
    // Check current generation
    if (generation?.s3Urls && generation.s3Urls.length > 0) {
      for (let i = 0; i < generation.s3Urls.length; i++) {
        const imageId = createImageId('current', i);
        if (imageId === selectedImageId) {
          return generation;
        }
      }
    }
    
    // Check generation history
    for (const gen of generationHistory) {
      for (let i = 0; i < gen.s3Urls.length; i++) {
        const imageId = createImageId(gen.id, i);
        if (imageId === selectedImageId) {
          return gen;
        }
      }
    }
    
    return null;
  };

  // Handle redirect if no data - must be after all hooks are called
  useEffect(() => {
    if (!hasInitialData) {
      navigate('/', { replace: true });
    }
  }, [navigate, hasInitialData]);

  // Enhanced toast management functions
  const showToastMessage = (message: string, type: ToastType = 'success', title?: string, persistent = false) => {
    setToastMessage(message);
    setToastType(type);
    setToastTitle(title);
    setToastPersistent(persistent);
    setShowToast(true);
  };

  const handleToastClose = () => {
    setShowToast(false);
    setToastTitle(undefined);
    setToastPersistent(false);
  };

  const handleCreateVideo = (selectedAvatars: string[]) => {
    // Track result-click event for submit-review button
    if (generation) {
      mixpanelService.trackResultClick(
        'submit-review',
        generation.prompt,
        generation.filters
      );
    }
    
    // Handle video creation with selected avatars - open submit modal
    openSubmitModal();
  };

  // Handle prompt change
  const handlePromptChange = (prompt: string) => {
    setCurrentInputPrompt(prompt);
  };

  // Copy prompt to clipboard
  const handleCopyPrompt = async (promptText: string) => {
    setCurrentInputPrompt(promptText);
    showToastMessage('Prompt copied to input field!', 'success');
  };

  const handleFiltersChange = (filters: FilterState) => {
    if (generation) {
      setGeneration({
        ...generation,
        filters
      });
    }
  };

  const handleFileChange = (file: File | null, base64: string | null) => {
    setUploadedFileBase64(base64);
  };

  // ResultPage event handlers (copied from ResultPage.tsx)
  const handleAvatarSelect = (imageId: string) => {
    // Toggle selection: if clicking the same image, unselect it
    if (selectedImageId === imageId) {
      setSelectedImageId(null);
    } else {
      setSelectedImageId(imageId);
      // Track result-click event for image selection
      if (generation) {
        mixpanelService.trackResultClick(
          'select',
          generation.prompt,
          generation.filters
        );
      }
    }
  };
  // Handle background click (unselect all images)
  const handleBackgroundClick = (e: React.MouseEvent) => {
    setSelectedImageId(null);
  };
  // Handle heart button click (toggle like state)
  const handleHeartClick = (imageId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent image selection
    
    // Toggle liked state in localStorage
    const isNowLiked = toggleLikedImage(imageId);
    
    // Track result-click event for like action
    if (generation) {
      mixpanelService.trackResultClick(
        'like',
        generation.prompt,
        generation.filters
      );
    }
    
    // Update local state
    setLikedImages(prev => {
      const newSet = new Set(prev);
      if (isNowLiked) {
        newSet.add(imageId);
      } else {
        newSet.delete(imageId);
      }
      return newSet;
    });
  };

  // Handle menu button click
  const handleMenuClick = (imageId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent image selection
    
    // Track result-click event for menu button
    if (generation) {
      mixpanelService.trackResultClick(
        'menu',
        generation.prompt,
        generation.filters
      );
    }
    
    setOpenMenuId(openMenuId === imageId ? null : imageId);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setOpenMenuId(null);
  };

  // Handle download
  const handleDownload = (imageUrl: string) => {
    // Track result-click event for download
    if (generation) {
      mixpanelService.trackResultClick(
        'download',
        generation.prompt,
        generation.filters
      );
    }
    window.open(imageUrl, '_blank', 'noopener,noreferrer');
  };

  // Handle submit as stock avatar
  const handleSubmitAsStock = (imageUrl: string) => {
    // Track result-click event for submit-stock-avatar
    if (generation) {
      mixpanelService.trackResultClick(
        'submit-stock-avatar',
        generation.prompt,
        generation.filters
      );
    }
    window.open('https://desk.bigvu.tv/welcome?utm_campaign=avatar_generator&utm_content=premium_features&utm_medium=modal&utm_source=prompt2avatar', '_blank');
  };

  // Modify handleGenerate to use the current input prompt
  const handleGenerate = async (isRetry = false) => {
    if (!generation) return;
    // Use currentInputPrompt instead of generation.prompt
    const promptToUse = currentInputPrompt.trim();
    if (!promptToUse) {
      showToastMessage('Please enter a prompt for your avatar.', 'warning', 'Missing Prompt');
      return;
    }
    // Set the prompt being generated
    setGeneratingPrompt(promptToUse);

    // Check rate limit before starting
    if (rateLimitStatus?.isBlocked || rateLimitStatus?.remainingUses === 0) {
      openOutOfCreditsModal();
      return;
    }
    
    // Track generate-start event
    mixpanelService.trackGenerateStart(
      promptToUse,
      generation.filters || {},
      !!uploadedFileBase64,
      true // hasResults: true for ResultsPage
    );
    
    // Reset error state if this is a new generation (not a retry)
    if (!isRetry) {
      setGenerationError(null);
    }
    
    setIsGenerating(true);
    setGenerationProgress(0);
    
    // Start 15-second progress timer
    const startTime = Date.now();
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const calculatedProgress = Math.min((elapsed / 18000) * 99, 99);
      setGenerationProgress(calculatedProgress);
      
      if (calculatedProgress >= 99) {
        clearInterval(progressInterval);
      }
    }, 50); // Update every 50ms for smooth animation
    
    try {
      // Make real API call to generate avatars (runs in parallel with progress timer)
      const response = await apiService.generateAvatars(
        promptToUse,
        generation.filters || {},
        uploadedFileBase64 || undefined
      );
      
      if (!response.success || !response.data) {
        throw new ApiServiceError('Generation failed', 500, 'GENERATION_ERROR');
      }
      
      // Wait for progress to reach 99% if it hasn't already
      const currentProgress = (Date.now() - startTime) / 18000 * 99;
      if (currentProgress < 99) {
        await new Promise(resolve => {
          const remainingTime = 18000 - (Date.now() - startTime);
          setTimeout(resolve, remainingTime);
        });
      }
      
      // Clear the interval and set to 100%
      clearInterval(progressInterval);
      setGenerationProgress(100);
      
      // Get model display name based on selected model
      const modelDisplayNames: Record<string, string> = {
        'flux-dev': 'FLUX.1 [dev]',
        'seedream-4': 'Seedream 4.0',
        'nano-banana': 'Nano Banana'
      };
      const selectedModel = generation.filters.model || 'flux-dev';
      const modelDisplayName = modelDisplayNames[selectedModel] || 'FLUX.1 [dev]';
      
      // Create real generation object from API response
      const updatedGeneration: AvatarGeneration = {
        ...generation,
        id: response.data.generationId,
        s3Urls: response.data.s3Urls,
        timestamp: new Date().toISOString(),
        prompt: response.data.promptInfo.original,
        enhancedPrompt: response.data.promptInfo.enhanced,
        modelUsed: modelDisplayName,
        model: selectedModel,
        usedEnhancement: response.data.promptInfo.usedEnhancement,
        filters: generation.filters
      };
      
      // Update rate limit from API response
      const updatedRateLimit = {
        remainingUses: response.data.remainingUses,
        usageCount: response.data.usageCount,
        resetDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        isBlocked: response.data.remainingUses === 0,
        weekStart: new Date().toISOString()
      };
      
      // Save both generation and rate limit atomically (single operation)
      localStorageManager.saveGenerationWithRateLimit(updatedGeneration, updatedRateLimit);

      // Update local state with fresh data
      setGeneration(updatedGeneration);
      setRateLimitStatus(updatedRateLimit);

      // Get the updated history from localStorage (excluding the current generation)
      const finalAppState = localStorageManager.getAppState();
      setGenerationHistory(finalAppState.generationHistory.slice(1));

      // Track generate-finish event
      mixpanelService.trackGenerateFinish(
        updatedGeneration.prompt,
        updatedGeneration.filters,
        !!uploadedFileBase64,
        true // hasResults: true for ResultsPage
      );
      
      // Show success message with usage info
      const remainingText = updatedRateLimit.remainingUses > 0 
        ? `${updatedRateLimit.remainingUses} generation${updatedRateLimit.remainingUses === 1 ? '' : 's'} remaining this week.`
        : 'Weekly limit reached. Resets on Sunday.';
      
      showToastMessage(
        `Successfully generated ${updatedGeneration.s3Urls.length} avatars! ${remainingText}`,
        'success',
        'Generation Complete'
      );
      
      // Reset retry count on success
      setGenerationError(null);
      
    } catch (error) {
      console.error('Generation failed:', error);
      
      // Clear the interval on error
      clearInterval(progressInterval);
      
      // Create comprehensive error info
      const errorInfo = createErrorInfo(error);
      setGenerationError(errorInfo);
      
      // Handle rate limit errors with modal, others with toast
      if (errorInfo.type === 'rate_limit') {
        openOutOfCreditsModal();
      } else {
        showToastMessage(
          errorInfo.message,
          'error',
          'Generation Failed',
          false
        );
      }
      
    } finally {
      setIsGenerating(false);
    }
  };

  // Show loading screen when generating
  if (isGenerating && generation) {
    return (
      <LoadingScreen 
        progress={generationProgress}
        prompt={generatingPrompt} // Use the generating prompt
        filters={generation.filters}
        rateLimitStatus={rateLimitStatus || undefined}
        hasUploadedFile={!!uploadedFileBase64}
        model={generation.filters.model}
      />
    );
  }

  // If no initial data, return null while redirecting (prevents loading spinner flash)
  if (!hasInitialData) {
    return null;
  }

  const selectedCount = selectedImageId ? 1 : 0;

  return (
    <>
      <div className="bg-white relative min-h-screen flex flex-col" data-name="Prompt2VideoAvatar" onClick={handleBackgroundClick}>
        {/* Header */}
        <div className="box-border content-stretch flex gap-4 items-center justify-start pb-0 pt-6 px-8 relative shrink-0 w-full max-w-[1440px] mx-auto" data-name="navbar">
          {/* BIGVU Icon */}
          <div className="h-[48px] w-[48px] relative shrink-0" data-node-id="2625:14095">
                <img alt="" className="block max-w-none size-full" src={ASSETS.BIGVU_ICON_BLUR} />
          </div> 

          {/* Title */}
          <div className="basis-0 font-['Inter:Bold',_sans-serif] font-bold grow leading-[0] min-h-px min-w-px not-italic relative shrink-0 text-[#253658] text-[24px] text-center hidden md:block">
            <p className="leading-[normal]">Select the portrait for your avatar</p>
          </div>

          {/* Right section - Credits and Avatar */}
          <div className="content-stretch flex gap-2 items-center justify-start relative shrink-0 ml-auto">
            {/* Credits - Using RateLimitStatus Component */}
            <div className="content-stretch flex flex-col gap-1 items-start justify-start relative shrink-0" data-name="Credits">
              <RateLimitStatus 
                rateLimitStatus={rateLimitStatus}
                compact={true}
                showUpgradePrompt={false}
                className="shrink-0 h-12"
              />
            </div>
          </div>
        </div>

        {/* Main Content - Scrollable Results */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          <div className="max-w-[1376px] mx-auto">
            <div className="flex flex-col gap-6">
              
              {/* Loading State for New Generation */}
              {isGenerating && (
                <div className="content-stretch flex flex-col gap-2 items-start justify-end relative rounded-[20px] shrink-0" data-name="Result">
                  {/* Prompt */}
                  <div className="content-stretch flex gap-1 items-center justify-start relative shrink-0 max-w-full" data-name="Prompt">
                    <button
                      onClick={() => generation?.prompt && handleCopyPrompt(generation.prompt)}
                      className="relative shrink-0 size-4 cursor-pointer hover:opacity-70 transition-opacity"
                      title="Copy prompt"
                      data-name="Copy-Text"
                    >
                      <div className="absolute left-[3.17px] size-[10.167px] top-[2.5px]" data-name="Union">
                        <img alt="Copy" className="block max-w-none size-full" src={imgCopyIcon} />
                      </div>
                    </button>
                    <div className="font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic relative text-[12px] text-[rgba(37,54,88,0.6)] flex-1 min-w-0">
                      <p className="leading-[1.4] overflow-ellipsis overflow-hidden whitespace-nowrap">
                        {generation?.prompt || "Generating new avatars..."}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Current Generation */}
              {!isGenerating && !generationError && generation?.s3Urls && generation.s3Urls.length > 0 && (
                <div className="content-stretch flex flex-col gap-2 items-start justify-end relative rounded-[20px] shrink-0" data-name="Result">
                  {/* Prompt */}
                  <div className="content-stretch flex gap-1 items-center justify-start relative shrink-0 max-w-full" data-name="Prompt">
                    <button
                      onClick={() => generation.prompt && handleCopyPrompt(generation.prompt)}
                      className="relative shrink-0 size-4 cursor-pointer hover:opacity-70 transition-opacity"
                      title="Copy prompt"
                      data-name="Copy-Text"
                    >
                      <div className="absolute left-[3.17px] size-[10.167px] top-[2.5px]" data-name="Union">
                        <img alt="Copy" className="block max-w-none size-full" src={imgCopyIcon} />
                      </div>
                    </button>
                    <div className="font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic relative text-[12px] text-[rgba(37,54,88,0.6)] flex-1 min-w-0">
                      <p className="leading-[1.4] overflow-ellipsis overflow-hidden whitespace-nowrap">
                        {generation.prompt || "Create a futuristic AI avatar with glowing blue eyes, sleek short hair, and subtle circuit patterns on the face. The style should be modern, minimalistic, and slightly cyberpunk."}
                      </p>
                    </div>
                  </div>

                  {/* Avatar Options */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 w-full" data-name="Options">
                    {generation.s3Urls.map((imageUrl, index) => {
                      const imageId = createImageId('current', index);
                      const isSelected = selectedImageId === imageId;
                      const isMenuOpen = openMenuId === imageId;
                      return (
                        <div key={imageId} className="content-stretch flex flex-col gap-1 items-center justify-center relative rounded-[15px] group" data-name="Generation content">
                          <div 
                            className={`aspect-[311/178] relative rounded-[15px] shrink-0 w-full cursor-pointer transition-all duration-200 overflow-hidden ${
                              isSelected ? 'ring-2 ring-[#4481fd]' : ''
                            }`} 
                            data-name="ThumbnailBaseWIP" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAvatarSelect(imageId);
                            }}
                          >
                            {/* Blurred background for aspect ratio filling */}
                            <div 
                              className="absolute inset-0 bg-center bg-cover bg-no-repeat blur-sm scale-110"
                              style={{ backgroundImage: `url('${imageUrl}')` }}
                            />
                            
                            {/* Main image with proper aspect ratio and fallback */}
                            <ImageWithFallback
                              src={imageUrl}
                              alt={`Generated avatar ${index + 1}`}
                              className="absolute inset-0 w-full h-full object-contain"
                              fallbackSrc={ASSETS.ALERT_ICON}
                            />
                            
                            {/* Heart Button */}
                            <ImageActionButton
                              icon={<HeartIcon filled={likedImages.has(imageId)} />}
                              onClick={(e) => handleHeartClick(imageId, e)}
                              isActive={likedImages.has(imageId)}
                              className="top-2 left-2 lg:left-auto lg:right-14"
                              title="Save to your collection"
                            />
                            
                            {/* Menu Button */}
                            <ImageActionButton
                              ref={(el) => { menuButtonRefs.current[imageId] = el; }}
                              icon={<MenuIcon />}
                              onClick={(e) => handleMenuClick(imageId, e)}
                              className="top-2 right-2"
                              title="More options"
                            />
                            
                            {/* Menu Dropdown */}
                            <ImageMenu
                              isOpen={isMenuOpen}
                              onClose={handleMenuClose}
                              onDownload={() => handleDownload(imageUrl)}
                              onSubmitAsStock={() => handleSubmitAsStock(imageUrl)}
                              buttonRef={menuButtonRefs.current[imageId] ? { current: menuButtonRefs.current[imageId]! } : undefined}
                              isMobileView={isMobileView}
                            />
                            
                            {/* Selection overlay */}
                            {isSelected && (
                              <div className="absolute inset-0 rounded-[15px] border-4 border-[#4481fd] pointer-events-none z-8"></div>
                            )}
                            
                            {/* Hover overlay with select button */}
                            {!isSelected && (
                              <div className="absolute inset-0 bg-[rgba(0,0,0,0.2)] rounded-[15px] opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center z-8 pointer-events-none">
                                <div className="bg-[#4481fd] box-border content-stretch flex gap-2 h-[52px] items-center justify-center p-[16px] rounded-[12px] pointer-events-auto" data-name="Button">
                                  <div className="capitalize flex flex-col font-['Inter:Bold',_sans-serif] font-bold justify-center leading-[0] not-italic relative shrink-0 text-[18px] text-center text-nowrap text-white">
                                    <p className="leading-[normal] whitespace-pre">Select</p>
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            <div aria-hidden="true" className="absolute border-[2.006px] border-[rgba(123,141,177,0.08)] border-solid inset-0 pointer-events-none rounded-[15px]" />
                          </div>

                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Previous Generations - All history with scroll */}
              {generationHistory.map((gen) => (
                <div key={gen.id} className="content-stretch flex flex-col gap-2 items-start justify-end relative rounded-[20px] shrink-0" data-name="Result">
                  {/* Prompt */}
                  <div className="content-stretch flex gap-1 items-center justify-start relative shrink-0 max-w-full" data-name="Prompt">
                    <button
                      onClick={() => handleCopyPrompt(gen.prompt)}
                      className="relative shrink-0 size-4 cursor-pointer hover:opacity-70 transition-opacity"
                      title="Copy prompt"
                      data-name="Copy-Text"
                    >
                      <div className="absolute left-[3.17px] size-[10.167px] top-[2.5px]" data-name="Union">
                        <img alt="Copy" className="block max-w-none size-full" src={imgCopyIcon} />
                      </div>
                    </button>
                    <div className="font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic relative text-[12px] text-[rgba(37,54,88,0.6)] flex-1 min-w-0">
                      <p className="leading-[1.4] overflow-ellipsis overflow-hidden whitespace-nowrap">
                        {gen.prompt}
                      </p>
                    </div>
                  </div>

                  {/* Avatar Options */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 w-full" data-name="Options">
                    {gen.s3Urls.slice(0, 4).map((url, avatarIndex) => {
                      const imageId = createImageId(gen.id, avatarIndex);
                      const isSelected = selectedImageId === imageId;
                      const isMenuOpen = openMenuId === imageId;
                      return (
                        <div key={imageId} className="content-stretch flex flex-col gap-1 items-center justify-center relative rounded-[15px] group" data-name="Generation content">
                          <div 
                            className={`aspect-[311/178] relative rounded-[15.048px] shrink-0 w-full cursor-pointer transition-all duration-200 overflow-hidden ${
                              isSelected ? 'ring-2 ring-[#4481fd]' : ''
                            }`} 
                            data-name="ThumbnailBaseWIP" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAvatarSelect(imageId);
                            }}
                          >
                            {/* Blurred background for aspect ratio filling */}
                            <div 
                              className="absolute inset-0 bg-center bg-cover bg-no-repeat blur-sm scale-110"
                              style={{ backgroundImage: `url('${url}')` }}
                            />
                            
                            {/* Main image with proper aspect ratio and fallback */}
                            <ImageWithFallback
                              src={url}
                              alt={`Generated avatar ${avatarIndex + 1}`}
                              className="absolute inset-0 w-full h-full object-contain"
                              fallbackSrc={ASSETS.ALERT_ICON}
                            />
                            
                            {/* Heart Button */}
                            <ImageActionButton
                              icon={<HeartIcon filled={likedImages.has(imageId)} />}
                              onClick={(e) => handleHeartClick(imageId, e)}
                              isActive={likedImages.has(imageId)}
                              className="top-2 left-2 lg:left-auto lg:right-14"
                              title="Save to your collection"
                            />
                            
                            {/* Menu Button */}
                            <ImageActionButton
                              ref={(el) => { menuButtonRefs.current[imageId] = el; }}
                              icon={<MenuIcon />}
                              onClick={(e) => handleMenuClick(imageId, e)}
                              className="top-2 right-2"
                              title="More options"
                            />
                            
                            {/* Menu Dropdown */}
                            <ImageMenu
                              isOpen={isMenuOpen}
                              onClose={handleMenuClose}
                              onDownload={() => handleDownload(url)}
                              onSubmitAsStock={() => handleSubmitAsStock(url)}
                              buttonRef={menuButtonRefs.current[imageId] ? { current: menuButtonRefs.current[imageId]! } : undefined}
                              isMobileView={isMobileView}
                            />
                            
                            {/* Selection overlay */}
                            {isSelected && (
                              <div className="absolute inset-0 rounded-[15px] border-4 border-[#4481fd] pointer-events-none z-8"></div>
                            )}
                            
                            {/* Hover overlay with select button */}
                            {!isSelected && (
                              <div className="absolute inset-0 bg-[rgba(0,0,0,0.2)] rounded-[15px] opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center z-8 pointer-events-none">
                                <div className="bg-[#4481fd] box-border content-stretch flex gap-2 h-[52px] items-center justify-center p-[16px] rounded-[12px] pointer-events-auto" data-name="Button">
                                  <div className="capitalize flex flex-col font-['Inter:Bold',_sans-serif] font-bold justify-center leading-[0] not-italic relative shrink-0 text-[18px] text-center text-nowrap text-white">
                                    <p className="leading-[normal] whitespace-pre">Select</p>
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            <div aria-hidden="true" className="absolute border-2 border-[rgba(123,141,177,0.08)] border-solid inset-0 pointer-events-none rounded-[15.048px]" />
                          </div>

                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Fixed Bottom Section - Input or Generate Moving Avatar Button */}
        <div className="sticky bottom-0 bg-white border-t border-gray-100 p-1 z-20" onClick = {e => e.stopPropagation()}>
          <div className="w-full">
            {selectedCount > 0 ? (
              /* Generate Moving Avatar Interface */
              <div className="w-full  lg:max-w-[720px] mx-auto">
                <div className="backdrop-blur-[20px] backdrop-filter bg-white box-border flex flex-col items-center justify-between p-2 pb-8 relative rounded-2xl w-full min-h-[160px]">
                  
                  {/* Selected Avatar Preview and Text - Centered */}
                  <div className="flex flex-col items-center justify-center relative shrink-0 w-full">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-90 h-80 rounded-lg overflow-hidden shrink-0 flex items-center justify-center">
                        <img 
                          src={getSelectedImageUrl() || ''} 
                          alt="Selected avatar" 
                          className="w-full h-full object-contain rounded-lg"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons - Bigger Generate Button */}
                  <div className="flex flex-wrap gap-2 items-end justify-center relative shrink-0 w-full pt-2">
                    {/* Generate Moving Avatar Button - Bigger and Centered */}
                    <button
                      onClick={() => handleCreateVideo([selectedImageId!])}
                      className="bg-[#4481fd] hover:bg-blue-600 transition-colors box-border flex gap-3 items-center justify-center px-8 py-3 relative rounded-xl shrink-0 h-12 text-lg shadow-lg"
                      title="Submit For Review"
                    >
                      <div className="font-inter font-bold text-white text-[16px] text-center text-nowrap">
                        Submit For Review
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* Enhanced Input Avatar Component - Connected directly to ResultsPage state */
              <EnhancedInputAvatar
                onPromptChange={handlePromptChange}
                onFiltersChange={handleFiltersChange}
                onFileChange={handleFileChange}
                initialPrompt={currentInputPrompt}
                initialFilters={generation?.filters}
                onGenerate={handleGenerate}
                isGenerating={isGenerating}
                rateLimitStatus={rateLimitStatus}
                disabled={isGenerating}
                isMobileView={isMobileView}
                hasResults={true} // hasResults: true for ResultsPage
              />
            )}
          </div>
        </div>

        {/* Popping Modal for Premium Features */}
        {/* <PoppingModal
          isOpen={isModalOpen}
          onClose={closePremiumModal}
          title="Unlock Premium Avatar Features"
          subtitle="Subscribe to AI Pro to access advanced features like moving portraits, stock avatar submission, and unlimited generations."
          onSubscribe={() => {
            window.open('https://desk.bigvu.tv/welcome?utm_campaign=avatar_generator&utm_content=premium_features&utm_medium=modal&utm_source=prompt2avatar', '_blank');
            closePremiumModal();
          }}
          onAlreadySubscribed={() => {
            window.open('https://desk.bigvu.tv/login?utm_campaign=avatar_generator&utm_content=already_subscribed&utm_medium=modal&utm_source=prompt2avatar', '_blank');
            closePremiumModal();
          }}
          showTimeline={true}
        /> */}
      </div>

      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        isVisible={showToast}
        onClose={handleToastClose}
        type={toastType}
        title={toastTitle}
        persistent={toastPersistent}
      />

      {/* Out of Credits Modal */}
      <OutOfCreditsModal
        isOpen={isOutOfCreditsOpen}
        onClose={closeOutOfCreditsModal}
        onBuyCredits={handleBuyCredits}
        title="You're out of credits!"
        subtitle="You've reached your weekly limit of 15 avatar generations. Subscribe to get unlimited access and create as many avatars as you want."
        isMobileView={isMobileView}
      />

      {/* Submit Modal */}
      <SubmitModal
        isOpen={isSubmitModalOpen}
        onClose={closeSubmitModal}
        imageUrl={getSelectedImageUrl() || ''}
        prompt={getSelectedImageGeneration()?.prompt || ''}
        filters={getSelectedImageGeneration()?.filters}
        hasReference={!!uploadedFileBase64}
        generationId={getSelectedImageGeneration()?.id}
        enhancedPrompt={getSelectedImageGeneration()?.enhancedPrompt}
        imagesUrl={getSelectedImageUrl() ? [getSelectedImageUrl()!] : []}
        onError={(message) => showToastMessage(message, 'error', 'Validation Error')}
      />
    </>
  );
}

export default ResultsPage;
